/**
 * Report Filters Component
 * Extracted from ReportingDashboard.jsx
 * Contains date range selectors and filter controls
 */
import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Loader2 } from 'lucide-react';

/**
 * Report Criteria Card
 * Main filter card for generating reports (Order Desk, Admin, Team Leaders)
 */
export const ReportCriteriaCard = ({
  filters,
  setFilters,
  isRunningReport,
  onGenerateReport,
  onReset,
  setReportResults,
  setReportError
}) => {
  // REPORT-SCOPE-001 — role-based scope visibility + default.
  // Tax TL → Tax only, Title Search TL → Title Search only, others → all 3.
  let scopeRole = '';
  try { scopeRole = JSON.parse(localStorage.getItem('user') || '{}').role || ''; } catch { /* noop */ }
  const scopeOptions = (scopeRole === 'tax_search_team_leader' || scopeRole === 'team_leader')
    ? [{ value: 'tax', label: 'Tax' }]
    : scopeRole === 'title_search_team_leader'
      ? [{ value: 'search', label: 'Title Search' }]
      : [
          { value: 'all', label: 'All Products' },
          { value: 'tax', label: 'Tax' },
          { value: 'search', label: 'Title Search' },
        ];
  const defaultScope = scopeOptions.length === 1 ? scopeOptions[0].value : '';

  const handleResetClick = () => {
    console.log('🔄 Reset button clicked - clearing date fields');
    setFilters(prev => ({ 
      ...prev, 
      customStartDate: null, 
      customEndDate: null,
      reportType: 'all',
      dateType: 'creation',
      // REPORT-SCOPE-001 — back to role-based default scope.
      moduleScope: defaultScope,
      // Feb 2026 — Option C toggle default ON (dashboard ↔ report parity).
      includeCarryForward: true,
    }));
    if (setReportResults) setReportResults(null);
    if (setReportError) setReportError(null);
    if (onReset) onReset();
  };

  const handleGenerateClick = () => {
    console.log('🔘 Generate Report button clicked!', {
      isRunningReport,
      customStartDate: filters.customStartDate,
      customEndDate: filters.customEndDate,
      disabled: isRunningReport || !filters.customStartDate || !filters.customEndDate || !filters.moduleScope
    });
    if (onGenerateReport) onGenerateReport();
  };

  return (
    <Card className="rounded-xl bg-white shadow-sm border border-slate-200 overflow-hidden" data-testid="report-filters-card">
      <CardContent className="px-6 py-5">
        {/* Single horizontal filter bar — premium SaaS layout */}
        <div className="flex flex-col xl:flex-row xl:items-end gap-4">
          {/* Filter inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
            {/* Begin Date */}
            <div className="space-y-1.5">
              <Label htmlFor="begin-date" className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Begin Date
              </Label>
              <Input
                id="begin-date"
                type="date"
                value={filters.customStartDate || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, customStartDate: e.target.value || null }))}
                className="h-10 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-colors"
                data-testid="report-begin-date"
              />
            </div>

            {/* End Date */}
            <div className="space-y-1.5">
              <Label htmlFor="end-date" className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                End Date
              </Label>
              <Input
                id="end-date"
                type="date"
                value={filters.customEndDate || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, customEndDate: e.target.value || null }))}
                className="h-10 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-colors"
                data-testid="report-end-date"
              />
            </div>

            {/* Report Type */}
            <div className="space-y-1.5">
              <Label htmlFor="report-type" className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Report Type
              </Label>
              <Select 
                value={filters.reportType || 'all'} 
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  reportType: value,
                  // REPORT-FIX-001: Completed reports default to the closure window
                  dateType: value === 'completed' ? 'closure' : prev.dateType,
                }))}
              >
                <SelectTrigger 
                  className="h-10 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                  data-testid="report-type-select"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="clarification">Clarification</SelectItem>
                  <SelectItem value="qc_review">QC Review</SelectItem>
                  <SelectItem value="mailaway">Mail-Away</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Report Scope — REPORT-SCOPE-001 (Jul 2026): All / Tax / Title Search */}
            <div className="space-y-1.5">
              <Label htmlFor="module-scope" className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Report Scope
              </Label>
              <Select 
                value={filters.moduleScope || ''} 
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  moduleScope: value,
                  // Changing scope resets Report Type and clears product
                  // selections that may be incompatible with the new module.
                  reportType: 'all',
                  clientProducts: [],
                  includeAllClientProducts: true,
                }))}
              >
                <SelectTrigger 
                  className="h-10 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                  data-testid="report-scope-select"
                >
                  <SelectValue placeholder="Select Report Scope" />
                </SelectTrigger>
                <SelectContent>
                  {scopeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`report-scope-option-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Type */}
            <div className="space-y-1.5">
              <Label htmlFor="date-type" className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Date Type
              </Label>
              <Select 
                value={filters.dateType || 'creation'} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, dateType: value }))}
              >
                <SelectTrigger 
                  className="h-10 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                  data-testid="date-type-select"
                >
                  <SelectValue placeholder="Select date type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="creation">Creation Date</SelectItem>
                  <SelectItem value="closure">Closure Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action buttons — right-aligned on desktop, full-width on mobile */}
          <div className="flex items-center gap-2 xl:ml-2">
            <Button 
              variant="outline"
              onClick={handleResetClick}
              className="h-10 px-4 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors font-medium text-sm"
              data-testid="report-reset-btn"
            >
              Reset
            </Button>
            <Button 
              onClick={handleGenerateClick}
              disabled={isRunningReport || !filters.customStartDate || !filters.customEndDate || !filters.moduleScope}
              className="h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm hover:shadow disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none transition-colors flex items-center gap-2 text-sm"
              data-testid="generate-report-btn"
            >
              {isRunningReport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating
                </>
              ) : (
                'Generate Report'
              )}
            </Button>
          </div>
        </div>

        {/* Carry-Forward toggle row (Feb 2026 — Option C).
            Default ON preserves the historical dashboard ↔ report
            parity: prior-period orders still in active workflow
            statuses (Open / Pending / Clarification / Mail-Away …)
            are $or'd into the window's result set. Operators who
            need strict date-range filtering clear the box. The flag
            is honoured across all four export paths (on-screen
            table, Order-Desk CSV, Advanced CSV/PDF, Consolidated
            Client Status Report). */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
          <Checkbox
            id="include-carry-forward"
            checked={filters.includeCarryForward !== false}
            onCheckedChange={(checked) =>
              setFilters((prev) => ({
                ...prev,
                includeCarryForward: checked === true,
              }))
            }
            data-testid="include-carry-forward-toggle"
          />
          <Label
            htmlFor="include-carry-forward"
            className="text-sm text-slate-700 cursor-pointer select-none"
          >
            Include Carry-Forward Orders
            <span className="ml-2 text-xs text-slate-400 font-normal">
              (active prior-period orders still in workflow)
            </span>
          </Label>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportCriteriaCard;
